import type { DirectoryPrivate } from '@repo/types/directory';

import { useQueryClient } from '@tanstack/react-query';

import { useBatchMutation } from '#batch-mutations/useBatchMutations';

import { DirectoryService } from './directoryService';
import { DirectoryKey } from './keys';

export function useDeleteDirectories(directoryId?: string) {
  const queryClient = useQueryClient();
  return useBatchMutation({
    mutationFn: (id: string) => DirectoryService.deleteDirectory(id).then((response) => response.data),
    onMutate: async (id) => {
      await queryClient.cancelQueries({
        queryKey: [DirectoryKey.DIRECTORY, directoryId],
      });
      const prevDirectory = queryClient.getQueryData<DirectoryPrivate>([DirectoryKey.DIRECTORY, directoryId]);

      if (prevDirectory) {
        queryClient.setQueryData<DirectoryPrivate>([DirectoryKey.DIRECTORY, directoryId], (prev) => {
          if (!prev) {
            return;
          }
          return {
            ...prev,
            files: prev.files.filter((file) => file.id !== id),
          };
        });
      }

      return { prevDirectory };
    },
    onError: (_err, _variables, context) => {
      if (context?.prevDirectory) {
        queryClient.setQueryData([DirectoryKey.DIRECTORY, directoryId], context.prevDirectory);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [DirectoryKey.DIRECTORY, directoryId],
      });
    },
  });
}
